import React from 'react';
import { ArrowRight, Award, Calendar, Facebook, Instagram } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Seo, breadcrumbJsonLd } from '@/components/Seo';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { SITE, PHOTOS } from '@/data/site-config';

export default function AboutPage() {
  const path = '/about-jarad-wingard';
  const breadcrumbs = [{ name: 'Home', path: '/' }, { name: 'About Jarad', path }];
  const personJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: SITE.owner,
    jobTitle: 'NASM-Certified Personal Trainer',
    worksFor: { '@type': 'HealthClub', name: SITE.name, url: SITE.publicUrl },
    address: {
      '@type': 'PostalAddress',
      addressLocality: SITE.city,
      addressRegion: SITE.stateAbbr,
      addressCountry: 'US',
    },
    sameAs: [SITE.social.facebook, SITE.social.instagram],
    hasCredential: [
      {
        '@type': 'EducationalOccupationalCredential',
        credentialCategory: 'Certification',
        recognizedBy: { '@type': 'Organization', name: 'National Academy of Sports Medicine' },
        name: 'NASM-Certified Personal Trainer',
      },
    ],
  };
  return (
    <>
      <Seo
        title={`About ${SITE.owner} — NASM-Certified Personal Trainer in Stuart, FL`}
        description={`${SITE.owner} is a NASM-certified personal trainer and owner of Fitness Training 360, a private personal training studio in ${SITE.city}, ${SITE.stateAbbr}. Serving ${SITE.county}.`}
        canonical={path}
        image={PHOTOS.jaradAtDesk}
        jsonLd={[breadcrumbJsonLd(breadcrumbs), personJsonLd]}
      />
      <Breadcrumbs items={breadcrumbs} />
      <section className="container-x py-10 grid gap-10 lg:grid-cols-12 items-start">
        <div className="lg:col-span-7">
          <span className="eyebrow"><Award size={12} /> NASM-Certified Personal Trainer</span>
          <h1 className="font-display text-4xl md:text-5xl font-bold mt-3">{SITE.owner}.</h1>
          <p className="text-lg text-[color:var(--ft-muted)] mt-4 max-w-2xl">Owner & lead trainer at Fitness Training 360 in {SITE.city}, {SITE.stateAbbr}. NASM-Certified Personal Trainer, longtime {SITE.county} local, and the coach behind the studio’s private 1-on-1 and small-group training.</p>

          <div className="surface p-5 mt-6">
            <p className="font-display text-lg italic">“{SITE.mission}”</p>
            <p className="text-sm text-[color:var(--ft-dim)] mt-2">— Fitness Training 360 mission</p>
          </div>

          <div className="prose-dark mt-7">
            <h2>Why I run a private studio</h2>
            <p>I’ve called this area home for a long time. Fitness Training 360 has been a fixture in the south {SITE.city} community, and when the opportunity came to take it over, it was a chance to build something around the way I believe training should feel — personal, focused, and welcoming to anyone who wants to work hard.</p>
            <h2>How I coach</h2>
            <p>Every program I write is built for the person in front of me — not the room. We work on what your body needs (strength, mobility, conditioning, all of it), at a pace that fits your life. I want clients to leave each session feeling like progress is actually happening — and like they were paid attention to the whole time.</p>
            <h2>What you can expect from me</h2>
            <ul>
              <li>One coach who knows your name and your story.</li>
              <li>NASM-grounded programming you understand, not a guessing game.</li>
              <li>A private studio that’s welcoming and judgment-free.</li>
              <li>An honest answer when something can be coached better another way.</li>
            </ul>
            <h2>Private personal training and small-group training</h2>
            <p>We offer both <Link className="underline" to="/services/personal-training">1-on-1 personal training</Link> and <Link className="underline" to="/services/semi-private-personal-training">semi-private (small-group) personal training</Link> out of the same private studio. Same coaching standard either way.</p>
            <h2>Community first</h2>
            <p>This is a small, local business in a small, local community. The studio is built around long-term client relationships — people I see grow over years, not weeks. If you want a coach who is genuinely invested in your training, this is the right place.</p>
          </div>

          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/contact" className="btn btn-primary btn-lg">Book a free consultation <ArrowRight size={18} /></Link>
            <a href={SITE.booking.url} target="_blank" rel="noreferrer" className="btn btn-secondary btn-lg"><Calendar size={18} /> Book on Vagaro</a>
          </div>
        </div>
        <aside className="lg:col-span-5 grid gap-4">
          <div className="surface overflow-hidden"><img src={PHOTOS.jaradAtDesk} alt={`${SITE.owner}, NASM-certified personal trainer and owner of ${SITE.name}, in his Stuart studio office`} className="w-full h-80 object-cover" /></div>
          <div className="surface overflow-hidden"><img src={PHOTOS.coachingClient} alt={`${SITE.owner} coaching a client during a personal training session at the private Stuart studio`} className="w-full h-72 object-cover" /></div>
          <div className="surface p-5">
            <h3 className="font-display text-sm font-semibold uppercase tracking-wider text-[color:var(--ft-dim)] mb-3">Follow {SITE.owner.split(' ')[0]} & FT360</h3>
            <div className="flex flex-wrap gap-2">
              <a href={SITE.social.facebookOwner} target="_blank" rel="noreferrer" className="fb-chip"><Facebook size={14} fill="#fff" /> Connect with Jarad</a>
              <a href={SITE.social.facebook} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm"><Facebook size={14} /> FT360 page</a>
              <a href={SITE.social.instagram} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm"><Instagram size={14} /> Instagram</a>
            </div>
          </div>
        </aside>
      </section>
    </>
  );
}
